import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  addVoucherCode,
  createUser,
  emptyCart,
  getCurrentUser,
  login,
  processTransaction,
  totalPrice,
  type CartDisplayValues,
  type CreateUserInput,
  type UserInfo,
} from '@/api/redeem';

/**
 * State machine for the redemption flow:
 *
 *   enter-code  →  choose-auth  →  signing-in   ─┐
 *                              ↘                  ├→ confirming → done
 *                                creating-account ┘
 *
 * Errors at any step show inline; the user can retry without losing
 * progress because the session-bound cart persists across calls.
 */
export type FlowStep =
  | 'enter-code'
  | 'choose-auth'
  | 'sign-in'
  | 'create-account'
  | 'confirming'
  | 'done';

export const useFlowStore = defineStore('flow', () => {
  const step = ref<FlowStep>('enter-code');
  const code = ref<string>('');
  const cart = ref<CartDisplayValues | null>(null);
  const cartTotal = ref<number>(0);
  const user = ref<UserInfo | null>(null);
  const busy = ref(false);
  const error = ref<string | null>(null);

  const productNames = computed(() => {
    const items = cart.value?.lineItems ?? [];
    const names = items
      .map((li) => (li.productName ?? '').toString().trim())
      .filter((n) => n.length > 0);
    // De-duplicate while preserving order
    return Array.from(new Set(names));
  });

  function reset(): void {
    step.value = 'enter-code';
    code.value = '';
    cart.value = null;
    cartTotal.value = 0;
    user.value = null;
    busy.value = false;
    error.value = null;
  }

  function clearError(): void {
    error.value = null;
  }

  // --- Step 1: code entry --------------------------------------------

  async function applyCode(rawCode: string): Promise<void> {
    busy.value = true;
    error.value = null;
    try {
      // Empty any prior cart contents so a re-entered code doesn't
      // accumulate line items in the same session.
      try { await emptyCart(); } catch { /* non-fatal */ }
      const trimmed = rawCode.trim();
      const display = await addVoucherCode(trimmed);
      cart.value = display;
      cartTotal.value = await totalPrice();
      code.value = trimmed;
      step.value = 'choose-auth';

      // If the user already has a session, skip the auth branch entirely.
      const existing = await getCurrentUser();
      if (existing && existing.userId) {
        user.value = existing;
        step.value = 'confirming';
        await finalize();
      }
    } catch (e: unknown) {
      error.value = extractError(e, 'That voucher code is not valid.');
    } finally {
      busy.value = false;
    }
  }

  // --- Step 2: auth --------------------------------------------------

  async function signIn(userName: string, password: string): Promise<void> {
    busy.value = true;
    error.value = null;
    try {
      const ok = await login(userName, password);
      if (!ok) {
        error.value = 'Invalid username or password.';
        return;
      }
      user.value = await getCurrentUser();
      step.value = 'confirming';
      await finalize();
    } catch (e: unknown) {
      error.value = extractError(e, 'Sign-in failed.');
    } finally {
      busy.value = false;
    }
  }

  async function signUp(input: CreateUserInput): Promise<void> {
    busy.value = true;
    error.value = null;
    try {
      const newUserId = await createUser(input);
      if (!newUserId) {
        error.value = 'Account creation failed.';
        return;
      }
      // Establish the session under the new credentials. userApi/create
      // doesn't always log the user in for this code path, so do it
      // explicitly.
      const ok = await login(input.userName, input.password);
      if (!ok) {
        error.value = 'Account created but sign-in failed. Try signing in.';
        step.value = 'sign-in';
        return;
      }
      user.value = await getCurrentUser();
      step.value = 'confirming';
      await finalize();
    } catch (e: unknown) {
      error.value = extractError(e, 'Account creation failed.');
    } finally {
      busy.value = false;
    }
  }

  // --- Step 3: finalize ----------------------------------------------

  async function finalize(): Promise<void> {
    busy.value = true;
    error.value = null;
    try {
      // Re-check the total. If the voucher didn't cover everything
      // we don't proceed — for the demo, we expect $0.
      const total = await totalPrice();
      cartTotal.value = total;
      if (total > 0) {
        error.value =
          'This voucher does not cover the full cost. Demo flow only handles $0 redemptions.';
        step.value = 'choose-auth';
        return;
      }
      await processTransaction();
      step.value = 'done';
    } catch (e: unknown) {
      error.value = extractError(e, 'Could not complete redemption.');
      // Give the user a chance to retry
      step.value = 'choose-auth';
    } finally {
      busy.value = false;
    }
  }

  // --- Navigation helpers --------------------------------------------

  function goToSignIn(): void {
    error.value = null;
    step.value = 'sign-in';
  }
  function goToCreateAccount(): void {
    error.value = null;
    step.value = 'create-account';
  }
  function goBackToCode(): void {
    error.value = null;
    step.value = 'enter-code';
  }
  function goBackToChooseAuth(): void {
    error.value = null;
    step.value = 'choose-auth';
  }

  return {
    step,
    code,
    cart,
    cartTotal,
    user,
    busy,
    error,
    productNames,
    applyCode,
    signIn,
    signUp,
    finalize,
    goToSignIn,
    goToCreateAccount,
    goBackToCode,
    goBackToChooseAuth,
    reset,
    clearError,
  };
});

function extractError(e: unknown, fallback: string): string {
  if (e && typeof e === 'object') {
    if ('errorList' in e && Array.isArray((e as { errorList: unknown }).errorList)) {
      const list = (e as { errorList: string[] }).errorList;
      if (list.length > 0) return list.join(' ');
    }
    if ('message' in e && typeof (e as { message: unknown }).message === 'string') {
      const msg = (e as { message: string }).message;
      if (msg) return msg;
    }
  }
  return fallback;
}
